import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileText,
  FolderOpen,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Target,
  Calendar,
  Upload,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { cn, formatDate } from '@/lib/utils';

export function ProjectDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<'ectd' | 'docs' | 'runs'>('ectd');

  const detail = useQuery({
    queryKey: ['project', id],
    queryFn: async () => (await api.get(`/api/projects/${id}`)).data,
  });

  const readiness = useQuery({
    queryKey: ['project', id, 'readiness'],
    queryFn: async () => (await api.get(`/api/projects/${id}/ectd-readiness`)).data,
    enabled: detail.data?.project?.type === 'REGULATORY_SUBMISSION',
  });

  if (detail.isLoading) return <Skeleton className="h-64" />;
  if (!detail.data) return <div>Not found</div>;

  const p = detail.data.project;
  const isSubmission = p.type === 'REGULATORY_SUBMISSION';
  const targetDate = p.targetSubmissionDate ? new Date(p.targetSubmissionDate) : null;
  const daysRemaining = targetDate ? Math.ceil((targetDate.getTime() - Date.now()) / 86400000) : null;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/projects">
            <ArrowLeft className="mr-2 h-4 w-4" /> Projects
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl">{p.name}</h1>
              <Badge variant={p.status === 'active' ? 'success' : 'secondary'}>{p.status}</Badge>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              <code>{p.slug}</code>
              {p.productName && ` · ${p.productName}`}
              {p.targetMarkets?.length > 0 && ` · ${p.targetMarkets.join(', ')}`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to={`/documents?projectId=${p.id}`}>
                <Upload className="mr-1.5 h-4 w-4" /> Upload docs
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Stat icon={FileText} label="Documents" value={p._count.documents} />
        <Stat icon={Clock} label="Awaiting review" value={detail.data.awaitingReview} tone={detail.data.awaitingReview > 0 ? 'warning' : 'muted'} />
        <Stat icon={CheckCircle2} label="Completed runs" value={detail.data.completedRuns} tone="success" />
        <Stat
          icon={Calendar}
          label="Target date"
          valueText={targetDate ? formatDate(targetDate) : 'Not set'}
          hint={daysRemaining !== null ? (daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days to go`) : undefined}
          tone={daysRemaining !== null && daysRemaining < 0 ? 'danger' : daysRemaining !== null && daysRemaining < 30 ? 'warning' : 'muted'}
        />
      </div>

      {isSubmission && (
        <div>
          <div className="flex gap-1 border-b">
            <Tab active={tab === 'ectd'} onClick={() => setTab('ectd')} label="eCTD readiness" icon={Target} />
            <Tab active={tab === 'docs'} onClick={() => setTab('docs')} label="Documents" icon={FileText} />
          </div>

          {tab === 'ectd' && (
            <div className="mt-4">
              {readiness.isLoading ? (
                <Skeleton className="h-64" />
              ) : !readiness.data?.readiness?.length ? (
                <EmptyState icon={Target} title="No eCTD modules yet" description="Assign documents to modules to see readiness status." />
              ) : (
                <EctdReadiness modules={readiness.data.readiness} />
              )}
            </div>
          )}
          {tab === 'docs' && (
            <div className="mt-4">
              <EmptyState
                icon={FolderOpen}
                title="Document view by module"
                description="Upload documents and assign them to eCTD modules. Use the Documents page with this project filter."
                action={{ label: 'Go to documents', href: `/documents?projectId=${p.id}` }}
              />
            </div>
          )}
        </div>
      )}

      {!isSubmission && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Linked work</CardTitle>
            <CardDescription>
              Documents and workflow runs tagged to this project.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button asChild variant="outline">
                <Link to={`/documents?projectId=${p.id}`}>View {p._count.documents} documents</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to={`/workflows?projectId=${p.id}`}>View {p._count.workflowRuns} runs</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function EctdReadiness({
  modules,
}: {
  modules: Array<{ module: string; name: string; documentCount: number; status: 'missing' | 'processing' | 'ready'; documents: Array<{ id: string; fileName: string; documentType: string | null }> }>;
}) {
  const total = modules.length;
  const ready = modules.filter((m) => m.status === 'ready').length;
  const pct = total > 0 ? Math.round((ready / total) * 100) : 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Overall readiness</div>
            <div className="mt-1 font-display text-3xl">{pct}%</div>
            <div className="text-xs text-muted-foreground">{ready} of {total} modules ready</div>
          </div>
          <div className="h-16 w-16 rounded-full border-[6px] border-primary/20 relative">
            <div
              className="absolute inset-0 rounded-full border-[6px] border-primary"
              style={{
                clipPath: `polygon(0 0, 100% 0, 100% ${pct}%, 0 ${pct}%)`,
              }}
            />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {modules.map((m) => {
          const tone =
            m.status === 'ready' ? 'border-emerald-500/40 bg-emerald-500/5' :
            m.status === 'processing' ? 'border-blue-500/40 bg-blue-500/5' :
            'border-destructive/30 bg-destructive/5';
          const Icon =
            m.status === 'ready' ? CheckCircle2 :
            m.status === 'processing' ? Clock :
            AlertTriangle;
          const iconTone =
            m.status === 'ready' ? 'text-emerald-600' :
            m.status === 'processing' ? 'text-blue-600' :
            'text-destructive';
          return (
            <Card key={m.module} className={cn(tone)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Icon className={cn('mt-0.5 h-5 w-5', iconTone)} />
                    <div>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs">Module {m.module}</code>
                        <span className="font-medium">{m.name}</span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {m.documentCount === 0 ? 'No documents assigned' : `${m.documentCount} document${m.documentCount > 1 ? 's' : ''}`}
                      </div>
                    </div>
                  </div>
                  <Badge variant={m.status === 'ready' ? 'success' : m.status === 'processing' ? 'info' : 'destructive'}>
                    {m.status}
                  </Badge>
                </div>
                {m.documents.length > 0 && (
                  <div className="mt-3 space-y-1 border-t pt-3 text-xs">
                    {m.documents.map((d) => (
                      <Link key={d.id} to={`/documents/${d.id}`} className="flex items-center gap-2 hover:text-primary">
                        <FileText className="h-3 w-3" />
                        <span className="truncate">{d.fileName}</span>
                        {d.documentType && <span className="text-muted-foreground">? {d.documentType}</span>}
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  valueText,
  hint,
  tone = 'muted',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value?: number;
  valueText?: string;
  hint?: string;
  tone?: 'muted' | 'success' | 'warning' | 'danger';
}) {
  const toneCls = {
    muted: 'bg-muted/40 text-muted-foreground',
    success: 'bg-emerald-500/10 text-emerald-600',
    warning: 'bg-amber-500/10 text-amber-600',
    danger: 'bg-destructive/10 text-destructive',
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-1 font-display text-xl">{value ?? valueText ?? '—'}</div>
          {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
        </div>
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-md', toneCls)}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}

function Tab({ active, onClick, label, icon: Icon }: { active: boolean; onClick: () => void; label: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        '-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
